import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { CheckCircle2 } from "lucide-react";

export const SERVICES = {
  "gros-oeuvre": {
    title: "Gros œuvre à Bernay et dans l'Eure",
    seoTitle: "Gros œuvre à Bernay & dans l'Eure | DMZ Normandie",
    seoDesc: "Travaux de gros œuvre près de Bernay : fondations, murs porteurs, dalles et structures béton. DMZ Normandie — devis gratuit, 07 45 04 91 89.",
    subtitle: "Le gros œuvre est la base de toute construction : fondations, murs porteurs, dalles et structures. DMZ Normandie réalise vos travaux de gros œuvre à Bernay, Évreux et dans l'Eure.",
    img: "/realisations/gros-oeuvre-batiment.webp",
    imgAlt: "Gros œuvre d'un bâtiment en parpaings réalisé par DMZ Normandie dans l'Eure",
    intro: "Une construction réussie repose sur un gros œuvre rigoureux. Nous étudions votre terrain et votre projet avant de réaliser les fondations, l'élévation des murs et les ouvrages en béton, dans les règles de l'art.",
    travaux: [
      "Fondations et semelles adaptées au terrain",
      "Élévation de murs porteurs en parpaing, brique ou biobrique",
      "Dalles béton et chapes",
      "Poteaux, linteaux, chaînages et ouvrages en béton",
      "Structures pour constructions neuves et extensions",
    ],
    projets: "Constructions neuves, extensions, agrandissements, garages, bâtiments professionnels.",
    faq: [
      { q: "Quels travaux font partie du gros œuvre ?", a: "Les fondations, les murs porteurs, les dalles et les ouvrages en béton : tout ce qui constitue la structure porteuse du bâtiment." },
      { q: "Intervenez-vous pour les particuliers et les professionnels ?", a: "Oui, nous réalisons du gros œuvre pour les particuliers comme pour les professionnels, à Bernay, Évreux et dans l'Eure." },
      { q: "Comment obtenir un devis de gros œuvre ?", a: "Contactez-nous par téléphone ou via le formulaire : nous étudions votre projet et vous remettons un devis gratuit et détaillé." },
    ],
    approche: "Notre approche : chaque chantier de gros œuvre commence par l'étude du terrain et du plan. Nous coordonnons terrassement, fondations et élévation pour garantir une structure stable, et nous vous tenons informé à chaque étape, du premier coup de pelle à la dalle terminée.",
    links: ["fondations", "construction-murs", "dalles-beton", "extension-maison"],
  },
  "construction-murs": {
    title: "Construction de murs à Bernay et dans l'Eure",
    seoTitle: "Construction de murs à Bernay | Murs & murets – DMZ Normandie",
    seoDesc: "Construction de murs et murets près de Bernay : murs porteurs, parpaings, briques, clôtures. DMZ Normandie — devis gratuit.",
    subtitle: "Murs porteurs, murs de clôture, murets et façades : DMZ Normandie construit vos murs en parpaing, brique ou biobrique à Bernay, Évreux et dans toute l'Eure.",
    img: "/realisations/mur-parpaing-interieur.webp",
    imgAlt: "Construction d'un mur en parpaings par DMZ Normandie, maçon près de Bernay",
    intro: "Un mur bien monté traverse le temps. Qu'il s'agisse d'un mur porteur, d'une clôture ou d'un muret, nous choisissons avec vous le matériau adapté à votre projet et à votre budget.",
    travaux: [
      "Murs porteurs en parpaing, brique ou biobrique",
      "Murets et murs de clôture avec piliers de portail",
      "Murs de façade et soubassements",
      "Réparation et reprise de murs existants",
      "Joints et finitions soignés",
    ],
    projets: "Maisons neuves, clôtures de terrain, garages, murets de jardin, reprises de murs abîmés.",
    faq: [
      { q: "Quel matériau choisir pour un mur : parpaing, brique ou biobrique ?", a: "Cela dépend de votre projet, de votre budget et des performances souhaitées. Nous vous conseillons lors de la visite sur place." },
      { q: "Construisez-vous des murs de clôture ?", a: "Oui : murets, murs de clôture et piliers de portail font partie de nos réalisations courantes dans l'Eure." },
      { q: "Réparez-vous les murs existants ?", a: "Oui, nous reprenons les murs fissurés ou abîmés : réparation, renfort localisé et rejointoiement." },
    ],
    approche: "Notre approche : nous commençons par vérifier l'alignement, les fondations et les contraintes du terrain. Chaque rang est monté au cordeau avec des joints réguliers, pour un mur droit, solide et propre dès la fin du chantier.",
    links: ["gros-oeuvre", "rejointoiement", "fondations", "renovation-bernay"],
  },
  fondations: {
    title: "Fondations à Bernay et dans l'Eure",
    seoTitle: "Fondations à Bernay | Semelles & maçonnerie de fondation – DMZ Normandie",
    seoDesc: "Fondations et semelles près de Bernay : préparation du terrain, maçonnerie de fondation adaptée au sol. DMZ Normandie — devis gratuit.",
    subtitle: "Des fondations adaptées au terrain sont la garantie d'un ouvrage durable. DMZ Normandie prépare et coule vos fondations à Bernay, Évreux et dans l'Eure.",
    img: "/realisations/pignons-parpaing.webp",
    imgAlt: "Maçonnerie de fondation et élévation de murs par DMZ Normandie dans l'Eure",
    intro: "Chaque terrain est différent : nature du sol, portance, présence d'eau. Nous étudions ces contraintes avant de dimensionner et réaliser vos fondations, pour une construction stable dans la durée.",
    travaux: [
      "Étude et préparation du terrain",
      "Semelles et fondations adaptées au sol",
      "Fouilles et terrassement de maçonnerie",
      "Coulage et ferraillage des fondations",
      "Fondations pour maisons, extensions, murs et murets",
    ],
    projets: "Constructions neuves, extensions, garages, murs de clôture, ouvrages extérieurs.",
    faq: [
      { q: "Comment sont dimensionnées les fondations ?", a: "Selon la nature de votre terrain et le poids de l'ouvrage. Nous étudions la configuration sur place avant de proposer une solution." },
      { q: "Faites-vous les fondations pour une extension ?", a: "Oui, nous réalisons les fondations des extensions et agrandissements, en liaison avec le bâtiment existant." },
      { q: "Les fondations sont-elles comprises dans un devis de construction ?", a: "Oui, notre devis détaille chaque poste : fondations, élévation, dalles et ouvrages béton." },
    ],
    approche: "Notre approche : avant de couler, nous analysons la nature du sol et le poids de l'ouvrage à porter. Ferraillage, profondeur et dimensions sont adaptés à votre terrain pour éviter tassements et fissures à long terme.",
    links: ["gros-oeuvre", "dalles-beton", "extension-maison", "construction-murs"],
  },
  "dalles-beton": {
    title: "Dalles et chapes béton à Bernay et dans l'Eure",
    seoTitle: "Dalle béton à Bernay | Chapes & béton lissé – DMZ Normandie",
    seoDesc: "Dalles béton, chapes, béton lissé et désactivé près de Bernay : terrasses, garages, intérieurs. DMZ Normandie — devis gratuit, 07 45 04 91 89.",
    subtitle: "Dalles, chapes, béton lissé ou désactivé : DMZ Normandie coule vos ouvrages en béton à Bernay, Évreux et dans toute l'Eure, pour l'intérieur comme pour l'extérieur.",
    img: "/realisations/terrasse-maison-beton.webp",
    imgAlt: "Dalle et terrasse en béton lissé coulées par DMZ Normandie dans l'Eure",
    intro: "Une dalle réussie demande une préparation rigoureuse : terrassement, coffrage, ferraillage, coulage puis finition. Nous maîtrisons chaque étape pour une surface plane, solide et durable.",
    travaux: [
      "Dalles béton pour maisons, garages et extensions",
      "Chapes de finition prêtes à recevoir un revêtement",
      "Béton lissé pour terrasses et surfaces contemporaines",
      "Béton désactivé pour allées et extérieurs",
      "Coffrage, ferraillage et finitions",
    ],
    projets: "Terrasses, dalles de garage, chapes intérieures, allées, bases d'abris et d'extensions.",
    faq: [
      { q: "Quelle différence entre béton lissé et béton désactivé ?", a: "Le béton lissé offre une surface unie et contemporaine ; le béton désactivé laisse apparaître les gravillons, avec un rendu plus texturé et antidérapant." },
      { q: "Réalisez-vous les dalles pour les extensions ?", a: "Oui, la dalle fait partie intégrante de nos chantiers d'extension, avec les fondations et l'élévation des murs." },
      { q: "Combien de temps avant d'utiliser une dalle béton ?", a: "Le béton durcit progressivement : nous vous indiquons les délais précis de circulation et de charge selon votre ouvrage." },
    ],
    approche: "Notre approche : une dalle réussie se joue dans la préparation. Nous soignons le terrassement, le coffrage et le ferraillage avant le coulage, puis la finition — lissée, balayée ou désactivée — selon l'usage prévu de la surface.",
    links: ["beton-amenagements-exterieurs", "fondations", "extension-maison", "gros-oeuvre"],
  },
  rejointoiement: {
    title: "Rejointoiement et réparation de maçonnerie à Bernay",
    seoTitle: "Rejointoiement & réparation de maçonnerie à Bernay | DMZ Normandie",
    seoDesc: "Rejointoiement, réparation de murs et reprises de maçonnerie à Bernay et dans l'Eure. Brique, pierre, silex. DMZ Normandie — devis gratuit.",
    subtitle: "Joints fatigués, murs fissurés, maçonnerie vieillissante : DMZ Normandie répare et rejointoie vos murs en brique, pierre ou silex à Bernay, Évreux et dans l'Eure.",
    img: "/realisations/facade-brique-silex.webp",
    imgAlt: "Rejointoiement et rénovation d'un mur en brique et silex par DMZ Normandie",
    intro: "Les maisons anciennes de notre région méritent des réparations respectueuses. Nous reprenons les joints, réparons les fissures et consolidons la maçonnerie existante en préservant le caractère du bâti.",
    travaux: [
      "Rejointoiement de murs en brique, pierre et silex",
      "Réparation de fissures et reprises de maçonnerie",
      "Remise en état de façades et murets",
      "Réparation de soubassements et clôtures",
      "Renforts localisés selon l'état du bâti",
    ],
    projets: "Façades anciennes, longères, murs de clôture, bâtiments en brique ou en pierre.",
    faq: [
      { q: "Quand faut-il rejointoyer un mur ?", a: "Lorsque les joints se creusent, s'effritent ou laissent passer l'eau. Un rejointoiement à temps protège le mur durablement." },
      { q: "Traitez-vous les fissures ?", a: "Oui : nous identifions d'abord la cause (tassement, humidité, vieillissement) avant de proposer la réparation adaptée." },
      { q: "Travaillez-vous sur les bâtiments anciens ?", a: "Oui, la rénovation de maçonnerie traditionnelle — brique, silex, pierre — fait partie de nos spécialités dans la région de Bernay." },
    ],
    approche: "Notre approche : nous utilisons des matériaux compatibles avec le bâti existant, notamment des mortiers adaptés aux murs anciens, afin de préserver la solidité et le caractère de votre bien.",
    links: ["renovation-bernay", "construction-murs", "maconnerie-bernay", "ouverture-mur-porteur"],
  },
  "parement-brique": {
    title: "Parement brique à Bernay et dans l'Eure",
    seoTitle: "Parement brique à Bernay & dans l'Eure | DMZ Normandie",
    seoDesc: "Pose de parement brique à Bernay, Évreux, Beuzeville et dans l'Eure : façades, pignons, murs intérieurs. DMZ Normandie — devis gratuit, 07 45 04 91 89.",
    subtitle: "Le parement brique habille vos murs avec le cachet de la brique normande : façades, pignons, murs intérieurs. DMZ Normandie pose vos parements à Bernay, Évreux, Beuzeville et dans toute l'Eure.",
    img: "/realisations/parement-brique-beuzeville-facade.webp",
    imgAlt: "Pose d'un parement brique sur façade à Beuzeville par DMZ Normandie",
    intro: "Le parement brique donne immédiatement du caractère à une construction ou une rénovation. C'est l'un de nos savoir-faire réguliers : nous préparons le support, posons chaque plaquette ou brique avec des joints réguliers et soignons les finitions des angles et des rives.",
    travaux: [
      "Pose de parement brique sur façades et pignons",
      "Plaquettes de parement en intérieur (murs, cheminées)",
      "Habillage de murs existants en rénovation",
      "Parement sur construction neuve",
      "Finitions d'angles, de rives et de baies",
    ],
    projets: "Façades de maisons, pignons, murs intérieurs, cheminées, rénovations de bâtis anciens.",
    faq: [
      { q: "Le parement brique se pose-t-il en intérieur et en extérieur ?", a: "Oui : en extérieur sur façades et pignons, en intérieur sur murs et cheminées. La préparation du support diffère, la pose reste soignée dans les deux cas." },
      { q: "Peut-on poser un parement sur un mur existant ?", a: "Oui, après vérification et préparation du support. Nous venons voir votre mur sur place pour valider la faisabilité." },
      { q: "Faites-vous beaucoup de parement brique ?", a: "Oui, c'est l'un de nos savoir-faire réguliers — comme notre chantier de parement brique réalisé à Beuzeville, visible dans nos réalisations." },
    ],
    approche: "Notre approche : nous contrôlons d'abord la planéité et la solidité du support. Puis pose rang par rang, au cordeau, avec des joints réguliers — un parement réussi se juge aux finitions des angles et des baies.",
    links: ["construction-murs", "renovation-bernay", "maconnerie-bernay", "zones"],
  },
};

const LINK_LABELS = {
  "maconnerie-bernay": "Maçonnerie à Bernay",
  "renovation-bernay": "Rénovation à Bernay",
  "maconnerie-evreux": "Maçonnerie à Évreux",
  "renovation-evreux": "Rénovation à Évreux",
  "gros-oeuvre": "Gros œuvre",
  "construction-murs": "Construction de murs",
  fondations: "Fondations",
  "dalles-beton": "Dalles et béton",
  "extension-maison": "Extension de maison",
  "ouverture-mur-porteur": "Ouverture de mur porteur",
  "beton-amenagements-exterieurs": "Béton & extérieurs",
  rejointoiement: "Rejointoiement & réparation",
  "parement-brique": "Parement brique",
  "maconnerie-beuzeville": "Maçonnerie à Beuzeville",
  "maconnerie-louviers": "Maçonnerie à Louviers",
  "maconnerie-conches-en-ouche": "Maçonnerie à Conches-en-Ouche",
  "maconnerie-pont-audemer": "Maçonnerie à Pont-Audemer",
  "maconnerie-thiberville": "Maçonnerie à Thiberville",
  "maconnerie-deauville": "Maçonnerie à Deauville",
  zones: "Zones d'intervention",
};

export function RelatedLinks({ slugs }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid="related-links">
      {slugs.map((s) => (
        <Link
          key={s}
          to={`/${s}`}
          className="group flex items-center gap-2 border border-white/10 px-5 py-3 text-sm font-light text-zinc-400 hover:border-[#C4A484] hover:text-[#C4A484] transition-colors duration-300"
          data-testid={`related-${s}`}
        >
          {LINK_LABELS[s]}
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      ))}
    </div>
  );
}

export default function ServicePage({ slug }) {
  const s = SERVICES[slug];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.title,
    provider: {
      "@type": "GeneralContractor",
      name: "DMZ Normandie",
      telephone: "+33745049189",
      email: "dmznormandie@gmail.com",
      url: "https://dmznormandie.fr",
      address: {
        "@type": "PostalAddress",
        streetAddress: "7 rue Jean de la Fontaine",
        postalCode: "27000",
        addressLocality: "Évreux",
        addressRegion: "Normandie",
        addressCountry: "FR",
      },
    },
    areaServed: ["Bernay", "Évreux", "Eure", "Normandie"],
  };

  return (
    <>
      <SEO title={s.seoTitle} description={s.seoDesc} path={`/${slug}`} jsonLd={jsonLd} />
      <PageHero title={s.title} subtitle={s.subtitle} />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mb-20">
            <Reveal>
              <SectionHead overline="Travaux réalisés" title="Ce que nous faisons" />
              <p className="-mt-6 mb-8 text-zinc-400 font-light leading-relaxed">{s.intro}</p>
              <p className="mb-8 text-zinc-400 font-light leading-relaxed">{s.approche}</p>
              <div className="space-y-4">
                {s.travaux.map((t) => (
                  <p key={t} className="flex items-start gap-3 text-zinc-300 font-light">
                    <CheckCircle2 size={18} className="text-[#C4A484] mt-0.5 shrink-0" /> {t}
                  </p>
                ))}
              </div>
              <p className="mt-8 text-sm font-light text-zinc-500 border-l-2 border-[#C4A484] pl-5">
                Projets concernés : {s.projets}
              </p>
              <p className="mt-4 text-sm font-light text-zinc-500 border-l-2 border-white/10 pl-5">
                Zone d'intervention : Bernay, Évreux, l'Eure et la Normandie selon les chantiers.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden relative">
                <img src={s.img} alt={s.imgAlt} loading="lazy" className="w-full h-80 lg:h-[420px] object-cover" />
                <div className="absolute inset-0 bg-black/25" />
              </div>
            </Reveal>
          </div>

          <div className="mb-20">
            <SectionHead overline="Questions fréquentes" title="Vos questions, nos réponses" />
            <FAQAccordion items={s.faq} testid={`faq-${slug}`} />
          </div>

          <Reveal>
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#C4A484] mb-5">Voir aussi</p>
            <RelatedLinks slugs={s.links} />
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
